import { useRouter } from 'next/router'

import cn from 'classnames'
import { Country as CountryList } from 'country-list'

import CategoryHierarchicalMenu from 'components/molecules/hierarchical-menu-custom'
import { HitsViewByType } from 'components/molecules/hits-view-by/constants'
import RangeInputCustomized from 'components/molecules/range-input-customized'
import RangeSelectList from 'components/molecules/range-select-list'
import RefinementListCustomized from 'components/molecules/refinement-list-customized'
import RefinementNumericMenu from 'components/molecules/refinement-numeric-menu'
import FilterGroupContainer from 'components/organisms/filter-group-container'
import RefinementListWrapper from 'components/organisms/refinement-list-wrapper'

import { useTranslate } from 'hooks'
import { FACTOR_FROM_CENT_TO_UNIT_CURRENCY } from 'hooks/currency/use-universial-price-conversion'
import useOnSalePercentageFilter from 'hooks/filters/use-on-sale-percentage-filter'
import useProductionLeadTimeFilter from 'hooks/filters/use-production-lead-time-filter'
import { Country } from 'hooks/retailer/use-countries'
import { useIsLoggedIn } from 'hooks/use-is-logged-in'

import { getInstantSearchHierarchicalAttributesLvs } from 'utils/search/attributes'

import { INSTANT_SEARCH_ATTRIBUTES } from 'constants/instantSearch'
import { ROUTES } from 'constants/routes'

import RefinementByQuery from '../refinement-by-query'
import { BESTSELLERS_QUERY, NEW_PUBLISHED_QUERY } from '../refinement-by-query/constants'
import { checkQueryExists } from '../refinement-by-query/utils'
import css from './filter-group.module.scss'

type FilterGroupProps = {
  viewBy: HitsViewByType
  rate: number
  currency: string
  countriesByCode: Record<string, CountryList | Country | undefined>
  isPromotionRoute: boolean
}

const FilterGroup = ({ viewBy, rate, currency, countriesByCode, isPromotionRoute }: FilterGroupProps) => {
  const { t } = useTranslate('strapi-search-[...term]')
  const router = useRouter()
  const { isLoggedIn } = useIsLoggedIn()
  const { getProductionLeadTimeRangeSelectInfos } = useProductionLeadTimeFilter()
  const { getOnSalePercentageRangeSelectInfos } = useOnSalePercentageFilter()

  const isProductionLeadTimeOn = checkQueryExists(router, INSTANT_SEARCH_ATTRIBUTES.PRODUCTION_LEAD_TIME.query)
  const isUniversalPriceOn = checkQueryExists(router, INSTANT_SEARCH_ATTRIBUTES.UNIVERSAL_PRICE.query)
  const isUniversalBrandMOVOn = checkQueryExists(router, INSTANT_SEARCH_ATTRIBUTES.UNIVERSAL_BRAND_MOV.query)
  const isBrandLocationFilterOn = checkQueryExists(router, `${INSTANT_SEARCH_ATTRIBUTES.BRAND_LOCATION.query}[0]`)
  const isProductMadeInFilterOn = checkQueryExists(router, `${INSTANT_SEARCH_ATTRIBUTES.MADE_IN.query}[0]`)
  const isOnSalePercentageOn = checkQueryExists(router, INSTANT_SEARCH_ATTRIBUTES.ON_SALE_PERCENTAGE.query)
  const isUniversalMsrpOn = checkQueryExists(router, INSTANT_SEARCH_ATTRIBUTES.UNIVERSAL_MSRP.query)

  return (
    <FilterGroupContainer viewBy={viewBy}>
      <RefinementListWrapper
        header={t('PageSearch.PageSearch_Filters_category')}
        type="list"
        isSwitchOpen={true}
        state={viewBy}
        data-test-id="filter-category"
      >
        <CategoryHierarchicalMenu
          attributes={getInstantSearchHierarchicalAttributesLvs()}
          separator=" > "
          isShowCount={viewBy === 'PRODUCT'}
        />
      </RefinementListWrapper>

      {!router.asPath.includes(ROUTES.SEARCH_PRODUCTS_NEW) && (
        <RefinementListWrapper
          header={`${t('PageSearch.PageSearch_Filters_new_arrivals')}`}
          type="list"
          isDefaultOpen={true}
          data-test-id="filter-new-published"
        >
          <RefinementByQuery filterQuery={NEW_PUBLISHED_QUERY} label={t('PageSearch.PageSearch_Filters_new_publish')} />
        </RefinementListWrapper>
      )}

      {!router.asPath.includes(ROUTES.SEARCH_PRODUCTS_BEST_SELLERS) && (
        <RefinementListWrapper
          header={`${t('PageSearch.PageSearch_Filters_shop_bestsellers')}`}
          type="list"
          isDefaultOpen={true}
          data-test-id="filter-best-sellers"
        >
          <RefinementByQuery filterQuery={BESTSELLERS_QUERY} label={t('PageSearch.PageSearch_Filters_bestsellers')} />
        </RefinementListWrapper>
      )}

      {viewBy === 'PRODUCT' && (
        <RefinementListWrapper
          header={t('PageSearch.PageSearch_Filters_brand')}
          type="list"
          isDefaultOpen={true}
          data-test-id="filter-brand"
        >
          <RefinementListCustomized
            attribute={INSTANT_SEARCH_ATTRIBUTES.BRAND_NAMES.attr}
            searchable
            searchablePlaceholder={t('PageSearch.PageSearch_Filters_search_for_brands')}
          />
        </RefinementListWrapper>
      )}

      <RefinementListWrapper
        header={`${t('PageSearch.PageSearch_Filters_on_sale_percentage')}`}
        type="list"
        isDefaultOpen={isOnSalePercentageOn}
        data-test-id="filter-on_sale_percentage"
        wrapperClassName={cn(isPromotionRoute && css.hideFilter)}
      >
        <RefinementNumericMenu
          attribute={INSTANT_SEARCH_ATTRIBUTES.ON_SALE_PERCENTAGE.attr}
          items={getOnSalePercentageRangeSelectInfos()}
        />
      </RefinementListWrapper>

      <RefinementListWrapper
        header={t('PageSearch.PageSearch_Filters_Product_Keywords')}
        type="list"
        isDefaultOpen={true}
        data-test-id="filter-product-keywords"
      >
        <RefinementListCustomized
          attribute={INSTANT_SEARCH_ATTRIBUTES.KEYWORDS.attr}
          searchable
          searchablePlaceholder={t('PageSearch.PageSearch_Filters_search_for_keywords')}
          getLabel={(item) => {
            return item.label
          }}
        />
      </RefinementListWrapper>

      <RefinementListWrapper
        header={t('PageSearch.PageSearch_Filters_brand_origin')}
        type="list"
        isDefaultOpen={isBrandLocationFilterOn}
        data-test-id="filter-brand-origin"
      >
        <RefinementListCustomized
          attribute={INSTANT_SEARCH_ATTRIBUTES.BRAND_LOCATION.attr}
          isShowCount={viewBy === 'PRODUCT'}
          getLabel={(item) => {
            const country = countriesByCode[item.label]
            return country ? country.name : item.label
          }}
        />
      </RefinementListWrapper>

      {viewBy !== 'BRAND' && (
        <RefinementListWrapper
          header={t('PageSearch.PageSearch_Filters_made_in')}
          type="list"
          isDefaultOpen={isProductMadeInFilterOn}
          data-test-id="filter-made-in"
        >
          <RefinementListCustomized
            attribute={INSTANT_SEARCH_ATTRIBUTES.MADE_IN.attr}
            getLabel={(item) => {
              const country = countriesByCode[item.label]
              return country ? country.name : item.label
            }}
          />
        </RefinementListWrapper>
      )}

      {viewBy === 'BRAND' && (
        <RefinementListWrapper
          header={`${t('PageSearch.PageSearch_Filters_minimum_order_value')} (${currency})`}
          type="list"
          isDefaultOpen={isUniversalBrandMOVOn}
        >
          <RangeInputCustomized
            attribute={INSTANT_SEARCH_ATTRIBUTES.UNIVERSAL_BRAND_MOV.attr}
            rangeFactor={rate}
            precision={5}
          />
        </RefinementListWrapper>
      )}

      {viewBy !== 'BRAND' && (
        <RefinementListWrapper
          header={`${t('PageSearch.PageSearch_Filters_production_lead_time')}`}
          type="list"
          isDefaultOpen={isProductionLeadTimeOn}
          data-test-id="filter-production-lead-time"
        >
          <RangeSelectList
            attribute={INSTANT_SEARCH_ATTRIBUTES.PRODUCTION_LEAD_TIME.attr}
            genRangeInfos={getProductionLeadTimeRangeSelectInfos}
          />
        </RefinementListWrapper>
      )}

      {viewBy !== 'BRAND' && (
        <RefinementListWrapper
          header={`${t('PageSearch.PageSearch_Filters_wholesale_price')} (${currency})`}
          type="list"
          isDefaultOpen={isUniversalPriceOn}
          data-test-id="filter-wholesale-price"
          // css hiding wholesale price filter to reduce Algolia query calls
          wrapperClassName={cn([!isLoggedIn && css.hideFilter])}
        >
          <RangeInputCustomized
            attribute={INSTANT_SEARCH_ATTRIBUTES.UNIVERSAL_PRICE.attr}
            rangeFactor={rate * FACTOR_FROM_CENT_TO_UNIT_CURRENCY}
          />
        </RefinementListWrapper>
      )}

      {viewBy !== 'BRAND' && (
        <RefinementListWrapper
          header={`${t('PageSearch.PageSearch_Filters_retail_price')} (${currency})`}
          type="list"
          isDefaultOpen={isUniversalMsrpOn}
          data-test-id="filter-msrp"
        >
          <RangeInputCustomized
            attribute={INSTANT_SEARCH_ATTRIBUTES.UNIVERSAL_MSRP.attr}
            rangeFactor={rate * FACTOR_FROM_CENT_TO_UNIT_CURRENCY}
          />
        </RefinementListWrapper>
      )}
    </FilterGroupContainer>
  )
}

export default FilterGroup
